// src/views/admin/UserManagementPage.js
// FlowSense MOW+ — User Management
//
// --- UPDATE (v2 · feature-rich user management + FlowSense AI) ----------------------------
//   Fixes
//   • Only 4 roles existed in the page (admin/staff/user/tech) — now every FlowSense role:
//     Administrator, Manager, Supervisor, Office Staff, Helpdesk Agent, Inspector,
//     Technician, Contractor, User (config/roles.js).
//   • New users were created through the PUBLIC /auth/register endpoint (no token) — now
//     POST /users (admin: any role; manager: roles below manager).
//   • The list loaded only 10 users (default page size) — now GET /users/directory (all).
//   • Edit could only change the role — now the full profile, status, role.
//   New
//   • Role overview cards (click to filter) · Directory with search / role / status / department
//     filters, sorting, paging, multi-select bulk status / role, CSV export
//   • Team by role (who are the managers, supervisors, helpdesk agents, inspectors,
//     technicians, contractors) · Roles & permissions matrix (what each role can do per module)
//   • Reset password (generated temporary password shown once, must-change flag) and
//     "Change my password" · Activate / deactivate / on leave · guarded delete
//   • User detail drawer: profile, permissions, activity (audit trail)
//   • FlowSense AI: team health score + findings (supervision span, missing roles, workload
//     outliers, temporary passwords, missing phones, inactive users holding work…), role advisor
//     in the form (suggests the role from the job title), Ask FlowSense AI (Ollama + rules)
// --- END UPDATE ---------------------------------------------------------------------------

import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import {
    Box, Flex, HStack, VStack, Text, Heading, Button, IconButton, Icon, Input, InputGroup, InputLeftElement, Select, Badge, Avatar,
    Table, Thead, Tbody, Tr, Th, Td, Checkbox, Menu, MenuButton, MenuList, MenuItem, MenuDivider, Tooltip, Tabs, TabList, Tab, TabPanels, TabPanel,
    SimpleGrid, Skeleton, Alert, AlertIcon, AlertDescription, useColorModeValue, useDisclosure, useToast, AlertDialog, AlertDialogOverlay,
    AlertDialogContent, AlertDialogHeader, AlertDialogBody, AlertDialogFooter, Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody,
    ModalFooter, useClipboard,
} from '@chakra-ui/react';
import {
    MdPeople, MdRefresh, MdDownload, MdSearch, MdMoreVert, MdEdit, MdLockReset, MdBlock, MdCheckCircle, MdDelete, MdVisibility,
    MdKey, MdFilterAltOff, MdChevronLeft, MdChevronRight, MdBeachAccess, MdContentCopy, MdCheck, MdAutoAwesome, MdGroups, MdSecurity, MdList,
} from 'react-icons/md';
import { FiUserPlus } from 'react-icons/fi';
import { getUserInfo } from '../../utils/auth';
import { ROLE_OPTIONS, canAssignRole, normalizeRole, roleMeta } from '../../config/roles';
import userAdminApi from '../../services/userAdminApi';
import UserFormModal from '../../components/users/UserFormModal';
import ResetPasswordModal from '../../components/users/ResetPasswordModal';
import UserDetailDrawer from '../../components/users/UserDetailDrawer';
import UserAiPanel from '../../components/users/UserAiPanel';
import { TeamByRole, PermissionsMatrix } from '../../components/users/RolesPanel';

const PAGE_SIZE = 25;
const STATUS = { active: { label: 'Active', scheme: 'green' }, inactive: { label: 'Inactive', scheme: 'gray' }, on_leave: { label: 'On leave', scheme: 'orange' } };
const relTime = (d) => {
    if (!d) return '—';
    const t = new Date(d).getTime(); if (!Number.isFinite(t)) return '—';
    const days = Math.floor((Date.now() - t) / 864e5);
    return days < 1 ? 'today' : days < 30 ? `${days}d ago` : new Date(d).toLocaleDateString();
};
const csvCell = (v) => { const s = v === null || v === undefined ? '' : String(v); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };

function CredentialsModal({ isOpen, onClose, user, password }) {
    const { hasCopied, onCopy, setValue } = useClipboard('');
    useEffect(() => { setValue(password || ''); }, [password, setValue]);
    const bg = useColorModeValue('gray.50', 'whiteAlpha.100');
    return (
        <Modal isOpen={isOpen} onClose={onClose} isCentered>
            <ModalOverlay />
            <ModalContent borderRadius="2xl">
                <ModalHeader>Account created · {user?.display_name}</ModalHeader>
                <ModalBody>
                    <Alert status="success" borderRadius="lg" mb={3}><AlertIcon />Give these sign-in details to {user?.display_name} securely. The password is shown only once.</Alert>
                    <VStack align="stretch" spacing={2} p={3} bg={bg} borderRadius="lg">
                        <HStack justify="space-between"><Text fontSize="sm" color="gray.500">Username</Text><Text fontFamily="mono" fontWeight="800">{user?.username}</Text></HStack>
                        <HStack justify="space-between"><Text fontSize="sm" color="gray.500">Temporary password</Text><Text fontFamily="mono" fontWeight="800" aria-label="Temporary password">{password}</Text></HStack>
                    </VStack>
                    <Text fontSize="xs" color="gray.500" mt={2}>They will be asked to choose their own password after signing in.</Text>
                </ModalBody>
                <ModalFooter gap={2}>
                    <Button leftIcon={<Icon as={hasCopied ? MdCheck : MdContentCopy} />} onClick={onCopy}>{hasCopied ? 'Copied' : 'Copy password'}</Button>
                    <Button colorScheme="purple" onClick={onClose}>Done</Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}

function UserManagementPage() {
    const toast = useToast();
    const me = useMemo(() => getUserInfo() || {}, []);
    const myRole = normalizeRole(me.role);
    const isManagement = ['admin', 'manager'].includes(myRole);

    const [users, setUsers] = useState([]);
    const [catalog, setCatalog] = useState(null);
    const [insights, setInsights] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [insightsLoading, setInsightsLoading] = useState(true);
    const [error, setError] = useState(null);

    const [search, setSearch] = useState('');
    const [roleFilter, setRoleFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState('all');
    const [deptFilter, setDeptFilter] = useState('all');
    const [idFilter, setIdFilter] = useState(null); // from a FlowSense finding
    const [sort, setSort] = useState('name_asc');
    const [page, setPage] = useState(1);
    const [selected, setSelected] = useState(new Set());
    const [tab, setTab] = useState(0);

    const [editing, setEditing] = useState(null);
    const [viewing, setViewing] = useState(null);
    const [resetting, setResetting] = useState(null);
    const [deleting, setDeleting] = useState(null);
    const [created, setCreated] = useState(null);
    const formModal = useDisclosure();
    const resetModal = useDisclosure();
    const selfPwModal = useDisclosure();
    const drawer = useDisclosure();
    const cancelRef = useRef(null);

    const cardBg = useColorModeValue('white', 'gray.800');
    const border = useColorModeValue('gray.200', 'gray.700');
    const headBg = useColorModeValue('gray.50', 'gray.900');
    const hoverRow = useColorModeValue('gray.50', 'whiteAlpha.50');
    const muted = useColorModeValue('gray.500', 'gray.400');
    const selRow = useColorModeValue('purple.50', 'whiteAlpha.100');

    const withSelf = useCallback((list) => list.map((u) => ({ ...u, is_self: String(u.id) === String(me.id) })), [me.id]);

    const loadInsights = useCallback(async () => {
        setInsightsLoading(true);
        try { setInsights(await userAdminApi.insights()); } catch { setInsights(null); } finally { setInsightsLoading(false); }
    }, []);

    const load = useCallback(async () => {
        setIsLoading(true); setError(null);
        try {
            const [list, cat] = await Promise.all([userAdminApi.directory(), userAdminApi.catalog().catch(() => null)]);
            setUsers(withSelf(Array.isArray(list) ? list : []));
            setCatalog(cat);
        } catch (e) {
            setError(e.status === 403 ? 'Only office roles (administrator, manager, supervisor, office staff, helpdesk) can open User Management.'
                : e.status === 404 ? 'The user-management API is not on this server yet — deploy backend/routes/users.js and services/userAdminService.js.' : e.message);
        } finally { setIsLoading(false); }
        loadInsights();
    }, [withSelf, loadInsights]);
    useEffect(() => { load(); }, [load]);

    const replaceUser = useCallback((u) => { if (u) setUsers((list) => withSelf(list.map((x) => (x.id === u.id ? { ...x, ...u } : x)))); }, [withSelf]);
    const canManage = useCallback((u) => isManagement && !!u && !u.is_self && (myRole === 'admin' || canAssignRole(myRole, u.role)), [isManagement, myRole]);

    // ── filtering ──
    const departments = useMemo(() => [...new Set(users.map((u) => u.department).filter(Boolean))].sort(), [users]);
    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        let l = users.filter((u) => (roleFilter === 'all' || u.role === roleFilter)
            && (statusFilter === 'all' || u.status === statusFilter)
            && (deptFilter === 'all' || u.department === deptFilter)
            && (!idFilter || idFilter.ids.includes(u.id))
            && (!q || [u.display_name, u.username, u.email, u.phone, u.department, u.job_title, u.employee_id, roleMeta(u.role).label].some((v) => String(v || '').toLowerCase().includes(q))));
        const by = {
            name_asc: (a, b) => String(a.display_name).localeCompare(String(b.display_name)),
            name_desc: (a, b) => String(b.display_name).localeCompare(String(a.display_name)),
            role: (a, b) => (b.role_level ?? 0) - (a.role_level ?? 0) || String(a.display_name).localeCompare(String(b.display_name)),
            newest: (a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0),
            workload: (a, b) => (b.open_work_orders ?? -1) - (a.open_work_orders ?? -1),
        };
        return [...l].sort(by[sort] || by.name_asc);
    }, [users, search, roleFilter, statusFilter, deptFilter, idFilter, sort]);
    useEffect(() => { setPage(1); }, [search, roleFilter, statusFilter, deptFilter, idFilter, sort]);
    const pages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    const pageRows = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
    const filtersOn = search || roleFilter !== 'all' || statusFilter !== 'all' || deptFilter !== 'all' || idFilter;
    const clearFilters = () => { setSearch(''); setRoleFilter('all'); setStatusFilter('all'); setDeptFilter('all'); setIdFilter(null); };

    const roleCounts = useMemo(() => {
        const c = {}; users.forEach((u) => { c[u.role] = c[u.role] || { total: 0, active: 0 }; c[u.role].total += 1; if (u.status === 'active') c[u.role].active += 1; }); return c;
    }, [users]);

    // ── actions ──
    const openCreate = (role) => { setEditing(role ? { __new: true, role } : null); formModal.onOpen(); };
    const openEdit = (u) => { setEditing(u); drawer.onClose(); formModal.onOpen(); };
    const openView = (u) => { setViewing(u); drawer.onOpen(); };
    const openReset = (u) => { setResetting(u); drawer.onClose(); resetModal.onOpen(); };

    const submitForm = async (body) => {
        if (editing && !editing.__new) {
            const u = await userAdminApi.updateProfile(editing.id, body);
            replaceUser(u); formModal.onClose();
            toast({ title: 'User updated', description: `${u.display_name} saved.`, status: 'success', duration: 3000 });
        } else {
            const r = await userAdminApi.create(body);
            setUsers((l) => withSelf([...l, { ...r.user, open_work_orders: 0 }]));
            formModal.onClose();
            if (r.temporary_password) setCreated({ user: r.user, password: r.temporary_password });
            else toast({ title: 'User created', description: `${r.user.display_name} can sign in now${r.must_change_password ? ' and will choose a new password' : ''}.`, status: 'success', duration: 4000 });
        }
        loadInsights();
    };

    const toggleStatus = async (u, next) => {
        const status = next || (u.status === 'active' ? 'inactive' : 'active');
        try {
            const r = await userAdminApi.setStatus(u.id, status);
            replaceUser(r); if (viewing?.id === u.id) setViewing({ ...viewing, ...r });
            toast({ title: status === 'active' ? 'Account activated' : status === 'on_leave' ? 'Marked on leave' : 'Account deactivated', description: r.display_name, status: 'success', duration: 2500 });
            loadInsights();
        } catch (e) { toast({ title: 'Not changed', description: e.message, status: 'error', duration: 5000 }); }
    };

    const confirmDelete = async () => {
        const u = deleting; setDeleting(null);
        try {
            await userAdminApi.remove(u.id);
            setUsers((l) => l.filter((x) => x.id !== u.id)); setSelected((s) => { const n = new Set(s); n.delete(u.id); return n; });
            toast({ title: 'User deleted', description: u.display_name, status: 'success', duration: 2500 });
            loadInsights();
        } catch (e) { toast({ title: 'Delete failed', description: e.message, status: 'error', duration: 6000 }); }
    };

    const runBulk = async (action, value) => {
        const ids = [...selected];
        try {
            const r = await userAdminApi.bulk(ids, action, value);
            (r.results || []).filter((x) => x.ok).forEach((x) => replaceUser(x.user));
            setSelected(new Set());
            toast({ title: `${r.updated} user(s) updated`, description: r.failed?.length ? `${r.failed.length} skipped: ${r.failed.slice(0, 3).map((f) => f.error).join('; ')}` : undefined, status: r.failed?.length ? 'warning' : 'success', duration: 5000 });
            loadInsights();
        } catch (e) { toast({ title: 'Bulk update failed', description: e.message, status: 'error', duration: 5000 }); }
    };

    const exportCsv = () => {
        const head = ['username', 'name', 'email', 'phone', 'role', 'role_label', 'status', 'department', 'job_title', 'employee_id', 'open_work_orders', 'must_change_password', 'created_at'];
        const rows = filtered.map((u) => [u.username, u.display_name, u.email, u.phone, u.role, roleMeta(u.role).label, u.status, u.department, u.job_title, u.employee_id, u.open_work_orders, u.must_change_password ? 'yes' : 'no', u.created_at].map(csvCell).join(','));
        const blob = new Blob([[head.join(','), ...rows].join('\n')], { type: 'text/csv;charset=utf-8' });
        const url = URL.createObjectURL(blob); const a = document.createElement('a');
        a.href = url; a.download = `flowsense-users-${new Date().toISOString().slice(0, 10)}.csv`; document.body.appendChild(a); a.click(); a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
    };

    const onInsightAction = (action) => {
        if (action.type === 'create') { if (isManagement) openCreate(action.role); else toast({ title: 'Ask an administrator or manager to add this account.', status: 'info' }); return; }
        if (action.type === 'filter') {
            if (action.ids) setIdFilter({ ids: action.ids, label: 'FlowSense finding' });
            if (action.role) setRoleFilter(action.role);
            setTab(0);
        }
    };

    const toggleSel = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
    const selectable = pageRows.filter(canManage);
    const allSel = selectable.length > 0 && selectable.every((u) => selected.has(u.id));

    return (
        <Box pt={{ base: '130px', md: '80px', xl: '80px' }} pb={10}>
            {/* header */}
            <Flex justify="space-between" align="center" mb={5} wrap="wrap" gap={3}>
                <HStack spacing={3}>
                    <Flex w="48px" h="48px" bgGradient="linear(to-br, purple.500, blue.600)" borderRadius="xl" align="center" justify="center" boxShadow="lg"><Icon as={MdPeople} color="white" boxSize={6} /></Flex>
                    <Box>
                        <Heading size="lg">User Management</Heading>
                        <Text fontSize="sm" color={muted}>Accounts, roles, permissions and passwords — explained by FlowSense AI.</Text>
                    </Box>
                </HStack>
                <HStack spacing={2} wrap="wrap">
                    <Tooltip label="Refresh"><IconButton icon={<Icon as={MdRefresh} />} variant="outline" onClick={load} isLoading={isLoading} aria-label="Refresh users" /></Tooltip>
                    <Tooltip label="Export the filtered list (CSV)"><IconButton icon={<Icon as={MdDownload} />} variant="outline" onClick={exportCsv} aria-label="Export users" isDisabled={!filtered.length} /></Tooltip>
                    <Button variant="outline" leftIcon={<Icon as={MdKey} />} onClick={selfPwModal.onOpen}>Change my password</Button>
                    {isManagement && <Button colorScheme="purple" leftIcon={<Icon as={FiUserPlus} />} onClick={() => openCreate()}>Add user</Button>}
                </HStack>
            </Flex>

            {error && <Alert status="error" borderRadius="xl" mb={4}><AlertIcon /><AlertDescription>{error}</AlertDescription></Alert>}

            {/* role overview */}
            <SimpleGrid columns={{ base: 2, md: 5, xl: 10 }} spacing={2} mb={5}>
                {ROLE_OPTIONS.map((r) => {
                    const c = roleCounts[r.id] || { total: 0, active: 0 };
                    const on = roleFilter === r.id;
                    return (
                        <Tooltip key={r.id} hasArrow label={r.description}>
                            <Box as="button" type="button" onClick={() => { setRoleFilter(on ? 'all' : r.id); setTab(0); }} textAlign="left" p={3} bg={cardBg} borderRadius="xl"
                                borderWidth="2px" borderColor={on ? `${r.color}.400` : border} _hover={{ borderColor: `${r.color}.300` }} aria-pressed={on} aria-label={`${r.label}: ${c.active} active`}>
                                <Text fontSize="2xs" fontWeight="800" textTransform="uppercase" color={`${r.color}.500`} noOfLines={1}>{r.short}</Text>
                                <Text fontSize="2xl" fontWeight="900" lineHeight="1.1">{isLoading && !users.length ? '…' : c.active}</Text>
                                <Text fontSize="2xs" color={muted}>{c.total !== c.active ? `${c.total - c.active} not active` : 'active'}</Text>
                            </Box>
                        </Tooltip>
                    );
                })}
            </SimpleGrid>

            {/* FlowSense AI */}
            <Box mb={5}><UserAiPanel insights={insights} isLoading={insightsLoading} onRefresh={loadInsights} onAction={onInsightAction} users={users} catalog={catalog} canCreate={isManagement} /></Box>

            <Tabs index={tab} onChange={setTab} variant="soft-rounded" colorScheme="purple" isLazy>
                <TabList mb={3} overflowX="auto">
                    <Tab><Icon as={MdList} mr={2} />Directory ({filtered.length})</Tab>
                    <Tab><Icon as={MdGroups} mr={2} />Team by role</Tab>
                    <Tab><Icon as={MdSecurity} mr={2} />Roles & permissions</Tab>
                </TabList>
                <TabPanels>
                    {/* DIRECTORY */}
                    <TabPanel p={0}>
                        <Box bg={cardBg} borderWidth="1px" borderColor={border} borderRadius="2xl" p={4}>
                            <Flex gap={2} mb={3} wrap="wrap" align="center">
                                <InputGroup maxW={{ base: '100%', md: '280px' }} size="sm">
                                    <InputLeftElement pointerEvents="none"><Icon as={MdSearch} color="gray.400" /></InputLeftElement>
                                    <Input placeholder="Search name, email, phone, title…" value={search} onChange={(e) => setSearch(e.target.value)} borderRadius="lg" aria-label="Search users" />
                                </InputGroup>
                                <Select size="sm" maxW="170px" value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} borderRadius="lg" aria-label="Role filter">
                                    <option value="all">All roles</option>
                                    {ROLE_OPTIONS.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
                                </Select>
                                <Select size="sm" maxW="140px" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} borderRadius="lg" aria-label="Status filter">
                                    <option value="all">Any status</option><option value="active">Active</option><option value="on_leave">On leave</option><option value="inactive">Inactive</option>
                                </Select>
                                {departments.length > 0 && (
                                    <Select size="sm" maxW="170px" value={deptFilter} onChange={(e) => setDeptFilter(e.target.value)} borderRadius="lg" aria-label="Department filter">
                                        <option value="all">All departments</option>{departments.map((d) => <option key={d} value={d}>{d}</option>)}
                                    </Select>
                                )}
                                <Select size="sm" maxW="150px" value={sort} onChange={(e) => setSort(e.target.value)} borderRadius="lg" aria-label="Sort">
                                    <option value="name_asc">Name A–Z</option><option value="name_desc">Name Z–A</option><option value="role">Role (highest first)</option><option value="newest">Newest</option><option value="workload">Most open work</option>
                                </Select>
                                {idFilter && <Badge colorScheme="purple" display="flex" alignItems="center" gap={1}><Icon as={MdAutoAwesome} />{idFilter.label}: {idFilter.ids.length}</Badge>}
                                {filtersOn && <Button size="sm" variant="ghost" leftIcon={<Icon as={MdFilterAltOff} />} onClick={clearFilters}>Clear</Button>}
                            </Flex>

                            {selected.size > 0 && (
                                <Flex mb={3} p={2} bg={selRow} borderRadius="lg" align="center" gap={2} wrap="wrap">
                                    <Text fontSize="sm" fontWeight="700">{selected.size} selected</Text>
                                    <Menu><MenuButton as={Button} size="xs">Set status</MenuButton><MenuList>
                                        <MenuItem onClick={() => runBulk('status', 'active')}>Active</MenuItem>
                                        <MenuItem onClick={() => runBulk('status', 'on_leave')}>On leave</MenuItem>
                                        <MenuItem onClick={() => runBulk('status', 'inactive')}>Inactive</MenuItem>
                                    </MenuList></Menu>
                                    <Menu><MenuButton as={Button} size="xs">Change role</MenuButton><MenuList>
                                        {ROLE_OPTIONS.filter((r) => canAssignRole(myRole, r.id)).map((r) => <MenuItem key={r.id} onClick={() => runBulk('role', r.id)}>{r.label}</MenuItem>)}
                                    </MenuList></Menu>
                                    <Button size="xs" variant="ghost" onClick={() => setSelected(new Set())}>Clear selection</Button>
                                </Flex>
                            )}

                            <Box overflowX="auto" borderWidth="1px" borderColor={border} borderRadius="xl">
                                <Table size="sm">
                                    <Thead bg={headBg}>
                                        <Tr>
                                            <Th w="32px" px={3}>{isManagement && <Checkbox isChecked={allSel} isIndeterminate={!allSel && selectable.some((u) => selected.has(u.id))} onChange={() => setSelected((s) => { const n = new Set(s); selectable.forEach((u) => (allSel ? n.delete(u.id) : n.add(u.id))); return n; })} aria-label="Select page" />}</Th>
                                            <Th>User</Th><Th>Contact</Th><Th>Role</Th><Th>Department / title</Th><Th>Status</Th><Th isNumeric>Open jobs</Th><Th>Joined</Th><Th w="48px" />
                                        </Tr>
                                    </Thead>
                                    <Tbody>
                                        {isLoading && !users.length ? [0, 1, 2, 3].map((i) => <Tr key={i}><Td colSpan={9}><Skeleton h="28px" /></Td></Tr>)
                                            : pageRows.length === 0 ? <Tr><Td colSpan={9} textAlign="center" py={8} color={muted}>{filtersOn ? 'No users match these filters.' : 'No users yet.'}</Td></Tr>
                                                : pageRows.map((u) => {
                                                    const m = roleMeta(u.role); const st = STATUS[u.status] || STATUS.active; const manage = canManage(u);
                                                    return (
                                                        <Tr key={u.id} _hover={{ bg: hoverRow }} bg={selected.has(u.id) ? selRow : undefined}>
                                                            <Td px={3}>{manage && <Checkbox isChecked={selected.has(u.id)} onChange={() => toggleSel(u.id)} aria-label={`Select ${u.display_name}`} />}</Td>
                                                            <Td>
                                                                <HStack spacing={3} as="button" type="button" onClick={() => openView(u)} textAlign="left" aria-label={`Open ${u.display_name}`}>
                                                                    <Avatar size="sm" name={u.display_name} bg={`${m.color}.500`} color="white" />
                                                                    <Box>
                                                                        <HStack spacing={1}><Text fontWeight="700" fontSize="sm">{u.display_name}</Text>{u.is_self && <Badge colorScheme="purple" fontSize="9px">You</Badge>}</HStack>
                                                                        <Text fontSize="xs" color={muted}>@{u.username}{u.employee_id ? ` · ${u.employee_id}` : ''}</Text>
                                                                    </Box>
                                                                </HStack>
                                                            </Td>
                                                            <Td><Text fontSize="sm">{u.email}</Text><Text fontSize="xs" color={u.phone ? muted : u.is_field ? 'orange.400' : muted}>{u.phone || (u.is_field ? 'no phone' : '—')}</Text></Td>
                                                            <Td>
                                                                <Tooltip hasArrow label={m.description}><Badge colorScheme={m.color} cursor="help">{m.label}</Badge></Tooltip>
                                                                {u.is_external && <Badge ml={1} variant="outline" colorScheme="yellow" fontSize="9px">external</Badge>}
                                                            </Td>
                                                            <Td><Text fontSize="sm" noOfLines={1}>{u.department || '—'}</Text><Text fontSize="xs" color={muted} noOfLines={1}>{u.job_title || ''}</Text></Td>
                                                            <Td>
                                                                <Badge colorScheme={st.scheme} variant="subtle">{st.label}</Badge>
                                                                {u.must_change_password && <Tooltip hasArrow label="Still on a temporary password"><Badge ml={1} colorScheme="yellow" variant="outline" fontSize="9px">temp pw</Badge></Tooltip>}
                                                            </Td>
                                                            <Td isNumeric fontWeight="700">{u.open_work_orders ?? '—'}</Td>
                                                            <Td fontSize="xs" color={muted}>{relTime(u.created_at)}</Td>
                                                            <Td>
                                                                <Menu isLazy placement="bottom-end">
                                                                    <MenuButton as={IconButton} size="sm" variant="ghost" icon={<Icon as={MdMoreVert} />} aria-label={`Actions for ${u.display_name}`} />
                                                                    <MenuList>
                                                                        <MenuItem icon={<Icon as={MdVisibility} />} onClick={() => openView(u)}>View details</MenuItem>
                                                                        {(manage || u.is_self) && <MenuItem icon={<Icon as={MdEdit} />} onClick={() => openEdit(u)}>Edit</MenuItem>}
                                                                        {manage && <MenuItem icon={<Icon as={MdLockReset} />} onClick={() => openReset(u)}>Reset password</MenuItem>}
                                                                        {u.is_self && <MenuItem icon={<Icon as={MdKey} />} onClick={selfPwModal.onOpen}>Change my password</MenuItem>}
                                                                        {manage && <MenuDivider />}
                                                                        {manage && u.status !== 'active' && <MenuItem icon={<Icon as={MdCheckCircle} />} onClick={() => toggleStatus(u, 'active')}>Activate</MenuItem>}
                                                                        {manage && u.status !== 'on_leave' && <MenuItem icon={<Icon as={MdBeachAccess} />} onClick={() => toggleStatus(u, 'on_leave')}>Mark on leave</MenuItem>}
                                                                        {manage && u.status !== 'inactive' && <MenuItem icon={<Icon as={MdBlock} />} onClick={() => toggleStatus(u, 'inactive')}>Deactivate</MenuItem>}
                                                                        {manage && <MenuItem icon={<Icon as={MdDelete} />} color="red.500" onClick={() => setDeleting(u)}>Delete</MenuItem>}
                                                                    </MenuList>
                                                                </Menu>
                                                            </Td>
                                                        </Tr>
                                                    );
                                                })}
                                    </Tbody>
                                </Table>
                            </Box>
                            {pages > 1 && (
                                <Flex justify="space-between" align="center" mt={3}>
                                    <Text fontSize="xs" color={muted}>{(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filtered.length)} of {filtered.length}</Text>
                                    <HStack spacing={1}>
                                        <IconButton size="sm" variant="ghost" icon={<Icon as={MdChevronLeft} />} isDisabled={page <= 1} onClick={() => setPage((p) => p - 1)} aria-label="Previous page" />
                                        <Text fontSize="xs">Page {page} / {pages}</Text>
                                        <IconButton size="sm" variant="ghost" icon={<Icon as={MdChevronRight} />} isDisabled={page >= pages} onClick={() => setPage((p) => p + 1)} aria-label="Next page" />
                                    </HStack>
                                </Flex>
                            )}
                        </Box>
                    </TabPanel>

                    {/* TEAM BY ROLE */}
                    <TabPanel p={0}>
                        <TeamByRole users={users} onOpenUser={openView} onFilterRole={(r) => { setRoleFilter(r); setTab(0); }} />
                    </TabPanel>

                    {/* ROLES & PERMISSIONS */}
                    <TabPanel p={0}>
                        <Text fontSize="sm" color={muted} mb={3}>
                            What each role can do in FlowSense. <b>Full</b> = everything incl. delete and approve · <b>Manage</b> = create, edit, assign, approve · <b>Edit</b> = create and update · <b>View</b> = read only · <b>Own</b> = only their own / assigned records (field apps).
                            {' '}Administrators can give any role; managers can give roles below manager.
                        </Text>
                        <PermissionsMatrix catalog={catalog} />
                    </TabPanel>
                </TabPanels>
            </Tabs>

            {/* modals */}
            <UserFormModal isOpen={formModal.isOpen} onClose={formModal.onClose} user={editing && !editing.__new ? editing : null} actorRole={myRole}
                onSubmit={submitForm} passwordMinLength={catalog?.password_policy?.min_length || 10} defaultRole={editing?.__new ? editing.role : null} />
            <ResetPasswordModal isOpen={resetModal.isOpen} onClose={() => { resetModal.onClose(); loadInsights(); }} user={resetting}
                onReset={async (body) => { const r = await userAdminApi.resetPassword(resetting.id, body); replaceUser(r.user); return r; }} passwordMinLength={catalog?.password_policy?.min_length || 10} />
            <ResetPasswordModal isOpen={selfPwModal.isOpen} onClose={selfPwModal.onClose} user={me} mode="self"
                onReset={async (body) => { await userAdminApi.changeMyPassword(body.current_password, body.new_password); toast({ title: 'Password changed', status: 'success', duration: 3000 }); return {}; }}
                passwordMinLength={catalog?.password_policy?.min_length || 10} />
            <UserDetailDrawer isOpen={drawer.isOpen} onClose={drawer.onClose} user={viewing ? users.find((x) => x.id === viewing.id) || viewing : null} canManage={canManage(viewing) || !!viewing?.is_self}
                onEdit={openEdit} onReset={openReset} onToggleStatus={(u) => toggleStatus(u)} />
            <CredentialsModal isOpen={!!created} onClose={() => setCreated(null)} user={created?.user} password={created?.password} />
            <AlertDialog isOpen={!!deleting} leastDestructiveRef={cancelRef} onClose={() => setDeleting(null)} isCentered>
                <AlertDialogOverlay>
                    <AlertDialogContent borderRadius="2xl">
                        <AlertDialogHeader>Delete {deleting?.display_name}?</AlertDialogHeader>
                        <AlertDialogBody>
                            This permanently removes the account. Their work orders and history keep their name. Consider <b>Deactivate</b> instead — it blocks sign-in and keeps everything.
                        </AlertDialogBody>
                        <AlertDialogFooter gap={2}>
                            <Button ref={cancelRef} onClick={() => setDeleting(null)}>Cancel</Button>
                            <Button variant="outline" onClick={() => { const u = deleting; setDeleting(null); toggleStatus(u, 'inactive'); }}>Deactivate instead</Button>
                            <Button colorScheme="red" onClick={confirmDelete}>Delete</Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>
        </Box>
    );
}

export default UserManagementPage;
